/**
 * MemberViewDialog Component
 *
 * Dialog for managers to view team member's objectives and milestones
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    Divider,
    LinearProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Person, Flag, Assignment } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { getScoreColor } from '../utils/scoreUtils';
import { IndividualObjective, Milestone } from '../../../types';

export const MemberViewDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { dialogs: dialogState, formState, closeMemberView } = dialogs;

    const member = formState.selectedMemberForView;

    // Get all individual objectives from team objectives for this member
    const allIndividualObjectives = (filteredObjectives.myDeptTeam ?? filteredObjectives.team).flatMap(
        (teamObj) => teamObj.individualObjectives || []
    );

    const memberObjectives = member
        ? allIndividualObjectives.filter(
              (obj) => String(obj.employee_id) === String(member.id)
          )
        : [];

    // Calculate overall stats
    const totalObjectives = memberObjectives.length;
    const totalMilestones = memberObjectives.reduce(
        (sum, obj) => sum + (obj.milestones?.length || 0),
        0
    );
    const avgScore =
        totalObjectives > 0
            ? memberObjectives.reduce((sum, obj) => sum + (parseFloat(String(obj.score)) || 0), 0) / totalObjectives
            : 0;
    const scoreColor = getScoreColor(avgScore);

    const memberName = member?.name || member?.firstName || 'Team Member';
    const memberInitial = memberName.charAt(0).toUpperCase();

    return (
        <Dialog
            open={dialogState.memberViewDialog}
            onClose={closeMemberView}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                    maxHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: `${scoreColor}20`,
                            color: scoreColor,
                            fontWeight: 700,
                            fontSize: '1.5rem',
                        }}
                    >
                        {memberInitial}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                            {memberName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            {totalObjectives} objective{totalObjectives !== 1 ? 's' : ''} | {totalMilestones} task{totalMilestones !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: scoreColor,
                            color: '#fff',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            boxShadow: `0 4px 12px ${scoreColor}40`,
                        }}
                    >
                        {Math.round(avgScore)}%
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {memberObjectives.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 6,
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                        }}
                    >
                        <Assignment sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            No Objectives Yet
                        </Typography>
                        <Typography variant="body2">
                            This team member hasn't been assigned any objectives.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {memberObjectives.map((objective: IndividualObjective) => {
                            const objScore = objective.score || 0;
                            const objScoreColor = getScoreColor(objScore);
                            const milestones = objective.milestones || [];

                            return (
                                <Box
                                    key={objective.id}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        bgcolor: isDarkMode ? '#0f172a' : '#f8fafc',
                                        border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                    }}
                                >
                                    {/* Objective Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                    mb: 0.5,
                                                }}
                                            >
                                                {objective.title}
                                            </Typography>
                                            {objective.team_title && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: isDarkMode ? '#64748b' : '#94a3b8' }}
                                                >
                                                    Team: {objective.team_title}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                bgcolor: `${objScoreColor}20`,
                                                color: objScoreColor,
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1.5,
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                            }}
                                        >
                                            {Math.round(objScore)}%
                                        </Box>
                                    </Box>

                                    {/* Progress Bar */}
                                    <LinearProgress
                                        variant="determinate"
                                        value={objScore}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                            mb: 2,
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: objScoreColor,
                                                borderRadius: 3,
                                            },
                                        }}
                                    />

                                    {/* Milestones */}
                                    {milestones.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                Tasks ({milestones.length})
                                            </Typography>
                                            {milestones.map((milestone: Milestone) => {
                                                const milestoneScore = (milestone.score || 0) * 100;
                                                const milestoneColor = getScoreColor(milestoneScore);
                                                const isCompleted = milestone.status === 'completed';

                                                return (
                                                    <Box
                                                        key={milestone.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: isDarkMode ? '#1e293b' : '#fff',
                                                            border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                                        }}
                                                    >
                                                        <Flag sx={{ color: milestoneColor, fontSize: 18 }} />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 500,
                                                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                                                    opacity: isCompleted ? 0.7 : 1,
                                                                }}
                                                            >
                                                                {milestone.title}
                                                            </Typography>
                                                            {milestone.due_date && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: isDarkMode ? '#64748b' : '#94a3b8' }}
                                                                >
                                                                    Due: {new Date(milestone.due_date).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.8rem',
                                                                fontWeight: 600,
                                                                color: milestoneColor,
                                                            }}
                                                        >
                                                            {Math.round(milestoneScore)}%
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: isDarkMode ? '#64748b' : '#94a3b8',
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            No tasks defined for this objective
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#e2e8f0' }} />

            <DialogActions sx={{ p: 2.5 }}>
                <Button
                    onClick={closeMemberView}
                    variant="contained"
                    sx={{
                        bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 4,
                        '&:hover': {
                            bgcolor: isDarkMode ? '#475569' : '#cbd5e1',
                        },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MemberViewDialog;
